import React, { useMemo, useRef, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FaTrash, FaPlus, FaChevronDown, FaChevronUp, FaFileImport, FaFileExport } from "react-icons/fa";
import { Menu } from "@headlessui/react";
import Papa from "papaparse";

const defaultTag = () => ({
  index: "",
  desc: "",
  plc: "UG1",
  area: "PE",
  db: "",
  byte: "",
  bit: "0",
  type: "bool",
  unidade: "",
  limiteMin: "",
  limiteMax: "",
  favorito: false,
});

const AREAS = ["PE", "PA", "MK", "DB", "CT", "TM"];
const TIPOS = ["bool", "byte", "int", "uint", "dint", "real"];
const BITS = ["0", "1", "2", "3", "4", "5", "6", "7"];
const PLCS = ["UG1", "UG2", "UG3"];
const ALL_FIELDS = [
  { id: "index", label: "Index" },
  { id: "desc", label: "Descrição" },
  { id: "plc", label: "PLC" },
  { id: "area", label: "Área" },
  { id: "byte", label: "Byte" },
  { id: "bit", label: "Bit" },
  { id: "type", label: "Type" },
];

export default function EditableTableModal() {
  const fileInputRef = useRef();
  const [data, setData] = useState(() =>
    Array.from({ length: 20 }, (_, i) => ({ ...defaultTag(), index: String(i + 1), desc: `Tag ${i + 1}` }))
  );
  const [toast, setToast] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [rowBeforeEdit, setRowBeforeEdit] = useState(null);
  const [draftRow, setDraftRow] = useState(null);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [expanded, setExpanded] = useState({});
  const [visibleFields, setVisibleFields] = useState(ALL_FIELDS.map(f => f.id));

  const columns = useMemo(() => [

{
  header: () => <span title="Favorito">★</span>,
  accessorKey: "favorito",
  id: "favorito",
  enableColumnFilter: false,
  cell: ({ row }) => (
    <div
      onClick={() => {
        const rowIndex = row.index;
        setData((prev) =>
          prev.map((r, i) =>
            i === rowIndex ? { ...r, favorito: !r.favorito } : r
          )
        );
      }}
      className="cursor-pointer text-yellow-400 text-lg text-center"
    >
      {row.original.favorito ? "★" : "☆"}
    </div>
  ),
},
    ...ALL_FIELDS.filter(col => visibleFields.includes(col.id)).map(col => ({
      header: col.label,
      accessorKey: col.id,
    })),
    {
      /*header: "Ações",*/
      header: "",
      id: "actions",
      cell: ({ row }) => (
        <div className="flex gap-2">
          <button onClick={() => handleExpand(row.id)} className="text-yellow-500">
            {row.getIsExpanded() ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        </div>
      ),
    },
  ], [expanded, visibleFields]);

  const handleEdit = (index) => {
    const current = data.find((row) => row.index === index);
    setRowBeforeEdit({ ...current });
    setDraftRow({ ...current });
    setEditingRow(index);
  };

  const handleSave = () => {
    setData((prev) =>
      prev.map((row) => (row.index === draftRow.index ? { ...draftRow } : row))
    );
    setEditingRow(null);
    setDraftRow(null);
    setRowBeforeEdit(null);
    setToast("Alterações salvas");
    setTimeout(() => setToast(""), 3000);
  };

  const handleCancel = () => {
    setData((prev) =>
      prev.map((row) => (row.index === rowBeforeEdit.index ? { ...rowBeforeEdit } : row))
    );
    setEditingRow(null);
    setDraftRow(null);
    setRowBeforeEdit(null);
    setToast("Alterações canceladas");
    setTimeout(() => setToast(""), 3000);
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
    pagination,expanded, columnFilters },
    onExpandedChange: setExpanded,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
  });

  const handleExpand = (rowId) => {
    setExpanded((prev) => {
      const isClosing = prev[rowId];
      const row = table.getRow(rowId);
      const index = row?.original?.index;

      if (isClosing && editingRow === index) {
        handleCancel();
        return { ...prev, [rowId]: false }; // Fechando com cancelamento
      }

      return { ...prev, [rowId]: !prev[rowId] };
    });
};

  const handleDelete = (rowIndex) => {
    setData((old) => old.filter((row) => row.index !== rowIndex));
  };

  const handleChange = (field, value) => {
    setDraftRow((prev) => ({ ...prev, [field]: value }));
  };

  /*
  const addTag = () => {
    setData((old) => [...old, { ...defaultTag(), index: String(old.length + 1) }]);
  };
  */

  const toggleField = (fieldId) => {
    setVisibleFields((current) =>
      current.includes(fieldId) ? current.filter(f => f !== fieldId) : [...current, fieldId]
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    if (file.name.endsWith(".json")) {
      reader.onload = event => {
        try {
          const json = JSON.parse(event.target.result);
          if (Array.isArray(json)) {
            setData(json);
          }
        } catch (err) {
          console.error("Erro ao importar JSON:", err);
        }
      };
      reader.readAsText(file);
    } else if (file.name.endsWith(".csv")) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: results => {
          setData(results.data);
        },
      });
    }
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "tags.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "tags.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  
  
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [newTag, setNewTag] = useState({
    index: "",
    desc: "",
    plc: "UG1",
    area: "PE",
    db: "",
    byte: "",
    bit: "0",
    type: "bool",
    unidade: "",
    limiteMin: "",
    limiteMax: "",
    favorito: false
  });

  /*
  const handleNewTagChange = (field, value) => {
    setNewTag(prev => ({ ...prev, [field]: value }));
  };
  */
  /*
  const handleSaveNewTag = () => {
    const newIndex = data.length > 0 ? String(Math.max(...data.map(t => parseInt(t.index || 0))) + 1) : "1";
    const nova = { ...newTag, index: newIndex };
    setData(prev => [...prev, nova]);
    setShowNewTagForm(false);
    setNewTag({
      index: "",
      desc: "",
      plc: "UG1",
      area: "PE",
      db: "",
      byte: "",
      bit: "0",
      type: "bool",
      unidade: "",
      limiteMin: "",
      limiteMax: "",
      favorito: false
    });
  };
  */

  const handleCancelNewTag = () => {
    setShowNewTagForm(false);
    setNewTag({
      index: "",
      desc: "",
      plc: "UG1",
      area: "PE",
      db: "",
      byte: "",
      bit: "0",
      type: "bool",
      unidade: "",
      limiteMin: "",
      limiteMax: "",
      favorito: false
    });
  };


  const handleNewTagChange = (field, value) => {
    setNewTag(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveNewTag = () => {
    setData(prev => [...prev, { ...newTag, index: String(prev.length + 1) }]);
    setShowNewTagForm(false);
    setNewTag({
      index: "",
      desc: "",
      plc: "UG1",
      area: "PE",
      db: "",
      byte: "",
      bit: "0",
      type: "bool",
      unidade: "",
      limiteMin: "",
      limiteMax: "",
      favorito: false
    });
  };


return (
  
    <div>
        <div className="relative z-40">

        <div>
            {toast && (
              <div className="fixed top-4 right-4 bg-gray-800 text-white px-4 py-2 rounded shadow z-50">
                {toast}
              </div>
            )}
            <div className="p-4 text-white bg-gray-900 min-h-screen">

              {/* ABAIXO - REGIÃO DOS BOTÕES*/}

              {/* <div className="mb-4 flex flex-wrap gap-4 items-center sticky top-0 z-50 bg-gray-900 py-2 shadow-md"> */}


                <div className="mb-4 sticky top-0 z-50 bg-gray-900 py-2 shadow-md px-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">

  
                      <div className="flex flex-wrap gap-2">
                        <Menu as="div" className="relative inline-block text-left">
                          <Menu.Button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                            <FaFileImport /> Importar Tags
                          </Menu.Button>
                          <Menu.Items className="absolute right-0 mt-1 w-40 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                            <div className="px-1 py-1">
                              <Menu.Item>
                                {({ active }) => (
                                  <label
                                    className={`${
                                      active ? 'bg-blue-500 text-white' : 'text-gray-900'
                                    } group flex w-full items-center rounded-md px-2 py-2 text-sm cursor-pointer`}
                                  >
                                    JSON (.json)
                                    <input
                                      type="file"
                                      accept=".json"
                                      onChange={handleFileChange}
                                      hidden
                                    />
                                  </label>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <label
                                    className={`${
                                      active ? 'bg-blue-500 text-white' : 'text-gray-900'
                                    } group flex w-full items-center rounded-md px-2 py-2 text-sm cursor-pointer`}
                                  >
                                    CSV (.csv)
                                    <input
                                      type="file"
                                      accept=".csv"
                                      onChange={handleFileChange}
                                      hidden
                                    />
                                  </label>
                                )}
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Menu>
                      
                      </div>

                      <div className="flex flex-wrap gap-2 ml-auto">
                      
                        <Menu as="div" className="relative">
                          <Menu.Button className="bg-gray-800 text-white px-4 py-2 rounded border border-gray-600">
                            <FaFileExport /> Exportar Tags
                          </Menu.Button>
                          <Menu.Items className="absolute right-2 mt-1 w-36 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                            <div className="px-1 py-1">
                              <Menu.Item>
                                {({ active }) => (
                                  <div className="flex flex-wrap gap-2 justify-end items-center mt-4">
                                    <button
                                      onClick={exportJSON}
                                      className={`${
                                        active ? 'bg-blue-500 text-white' : 'text-gray-900'
                                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                      JSON (.json)
                                    </button>
                                  </div>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <div className="flex flex-wrap gap-2 justify-end items-center mt-4">
                                    <button
                                      onClick={exportCSV}
                                      className={`${
                                        active ? 'bg-blue-500 text-white' : 'text-gray-900'
                                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                      CSV (.csv)
                                    </button>
                                  </div>
                                )}
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Menu>

                      
                                     
                  

                        <Menu as="div" className="relative">
                          <Menu.Button className="bg-gray-800 text-white px-4 py-2 rounded border border-gray-600">
                            Selecionar Campos
                          </Menu.Button>
                          {/* "absolute mt-1 right-0 w-48 bg-gray-900 border border-gray-700 rounded shadow-lg z-10" */}
                          <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-gray-900 border border-gray-700 rounded shadow-lg z-10">
                            {ALL_FIELDS.map(f => (
                              <Menu.Item key={f.id}>
                                {({ active }) => (
                                  <label className={`flex items-center px-3 py-2 text-sm cursor-pointer ${active ? 'bg-gray-700' : ''}`}>
                                    <input
                                      type="checkbox"
                                      checked={visibleFields.includes(f.id)}
                                      onChange={() => toggleField(f.id)}
                                      className="mr-2 accent-green-500"
                                    />
                                    {f.label}
                                  </label>
                                )}
                              </Menu.Item>
                            ))}
                          </Menu.Items>
                        </Menu>

                
                        {showNewTagForm && (
                          
                          <div className="w-full mt-4">
                          
                            <div className="bg-gray-800 p-4 rounded shadow-md">
                              <h3 className="text-white font-semibold mb-2">Nova Tag</h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                <input value={newTag.desc} onChange={(e) => handleNewTagChange("desc", e.target.value)} type="text" placeholder="Descrição" className="p-2 rounded bg-gray-700 text-white" />
                                <select value={newTag.plc} onChange={(e) => handleNewTagChange("plc", e.target.value)} className="p-2 rounded bg-gray-700 text-white">{PLCS.map(plc => <option key={plc}>{plc}</option>)}</select>
                                <select value={newTag.area} onChange={(e) => handleNewTagChange("area", e.target.value)} className="p-2 rounded bg-gray-700 text-white">{AREAS.map(area => <option key={area}>{area}</option>)}</select>
                                <input value={newTag.byte} onChange={(e) => handleNewTagChange("byte", e.target.value)} type="number" placeholder="Byte" className="p-2 rounded bg-gray-700 text-white" />
                                <select value={newTag.bit} onChange={(e) => handleNewTagChange("bit", e.target.value)} className="p-2 rounded bg-gray-700 text-white">{BITS.map(b => <option key={b}>{b}</option>)}</select>
                                <select value={newTag.type} onChange={(e) => handleNewTagChange("type", e.target.value)} className="p-2 rounded bg-gray-700 text-white">{TIPOS.map(t => <option key={t}>{t}</option>)}</select>

                                <input value={newTag.unidade} onChange={(e) => handleNewTagChange("unidade", e.target.value)} type="number" placeholder="Unidade" className="p-2 rounded bg-gray-700 text-white" />
                                <input value={newTag.limiteMin} onChange={(e) => handleNewTagChange("limiteMin", e.target.value)} type="number" placeholder="Limite Mínimo" className="p-2 rounded bg-gray-700 text-white" />
                                <input value={newTag.limiteMax} onChange={(e) => handleNewTagChange("limiteMax", e.target.value)} type="number" placeholder="Limite Máximo" className="p-2 rounded bg-gray-700 text-white" />


                              </div>
                              <div className="mt-4 flex justify-end gap-2">
                                <button onClick={handleSaveNewTag} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">Salvar</button>
                                <button onClick={handleCancelNewTag} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Cancelar</button>
                              </div>
                            </div>
                          </div>
                        )}

                        <button onClick={() => setShowNewTagForm(true)} className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2">
                          <FaPlus /> Adicionar Tag
                        </button>


                      </div>
              
                </div>
              </div>

              {/* </div>  <---------<<< */} 


              {/* ACIMA - REGIÃO DOS BOTÕES */}

              {/* ABAIXO - TABELA */}
              
              <div className="overflow-auto max-h-[500px]">
                <table className="min-w-full border border-gray-700 text-sm">
                  <thead className="sticky top-0 bg-gray-800 z-10">
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <th key={header.id} className="px-2 py-1 border border-gray-700 text-left">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getCanFilter?.() && (
                              <input
                                type="text"
                                value={header.column.getFilterValue() ?? ""}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                placeholder="Filtrar..."
                                className="mt-1 w-full px-1 py-0.5 bg-gray-800 text-white border border-gray-600 rounded"
                              />
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map(row => (
                      <React.Fragment key={row.id}>
                        <tr className="even:bg-gray-800 hover:bg-gray-700">
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id} className="px-2 py-1 border border-gray-700">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                        {row.getIsExpanded() && (
                          <tr className="bg-gray-950">
                            <td colSpan={columns.length} className="p-2 border-t border-gray-700">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {Object.keys(defaultTag()).map((field) => (
                                  field !== "index" && field !== "favorito" && (
                                    <div key={field}>
                                      <label className="block text-sm text-gray-400 capitalize">{field}</label>
                                      {["plc", "area", "bit", "type"].includes(field) ? (
                                        <select
                                          className="w-full p-1 bg-gray-800 text-white border border-gray-600 rounded"
                                          value={draftRow?.[field] ?? row.original[field]}
                                          onChange={(e) => handleChange(field, e.target.value)}
                                          disabled={editingRow !== row.original.index}
                                        >
                                          {(field === "plc" ? PLCS : field === "area" ? AREAS : field === "bit" ? BITS : TIPOS).map((v) => (
                                            <option key={v} value={v}>{v}</option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          className="w-full p-1 bg-gray-800 text-white border border-gray-600 rounded"
                                          value={draftRow?.[field] ?? row.original[field]}
                                          onChange={(e) => handleChange(field, e.target.value)}
                                          disabled={editingRow !== row.original.index}
                                        />
                                      )}
                                    </div>
                                  )
                                ))}
                              </div>
                              <div className="flex gap-3 mt-4 justify-end">
                                {editingRow !== row.original.index && (
                                  <button onClick={() => handleEdit(row.original.index)} className="bg-blue-600 px-3 py-1 rounded text-white flex items-center gap-2">
                                    Editar
                                  </button>
                                )}
                                {editingRow === row.original.index && (
                                  <>
                                    <button onClick={handleSave} className="bg-green-600 px-3 py-1 rounded text-white flex items-center gap-2">
                                      Salvar
                                    </button>
                                    <button onClick={handleCancel} className="bg-gray-600 px-3 py-1 rounded text-white flex items-center gap-2">
                                      Cancelar
                                    </button>
                                  </>
                                )}
                                <button onClick={() => handleDelete(row.original.index)} className="bg-red-700 px-3 py-1 rounded text-white flex items-center gap-2">
                                  <FaTrash /> Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            
            <div className="flex justify-between items-center mt-4">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
              >
                Página anterior
              </button>
              <div className="text-sm flex items-center gap-2">
                Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
                <select
                  className="ml-2 bg-gray-800 border border-gray-600 rounded text-white px-2 py-1"
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                >
                  {[5, 10, 20, 50].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      Mostrar {pageSize}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
              >
                Próxima página
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  
  );
}